import type { MessagePublic, MessageSend } from '@repo/types/message';

import { useChatFileUpload } from '@repo/api/chat/useChatFIleUpload';
import { usePostReadEntityNotifications } from '@repo/api/notification/usePostReadEntityNotification';
import { cn } from '@repo/lib/cn';
import { FileTypeUtils } from '@repo/lib/fs/fileTypeUtils';
import { prettyBytes } from '@repo/lib/prettyBytes';
import { FileType } from '@repo/types/enums/fileType';
import { NotificationType } from '@repo/types/enums/notificationType';
import { RotateCcwIcon } from 'lucide-react';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import ChatContent from '@/components/features/chat/layout/ChatContent';
import ChatFooter from '@/components/features/chat/layout/ChatFooter';
import ChatHeader from '@/components/features/chat/layout/ChatHeader';
import MediaViewer from '@/components/features/media/MediaViewer';
import { Button } from '@/components/ui/button';
import { useFileUpload } from '@/hooks/file-upload';
import { chatSocketService } from '@/services/chat-service';
import { useCredentials } from '@/store/auth-store';
import { useChatStore } from '@/store/chat-store';

export interface messageFormProps {
  content: string;
  replyingTo: MessagePublic | null;
}
export type messageFormTextProps = Omit<messageFormProps, 'files'>;
export type messageFormMediaProps = messageFormProps;

export const CHAT_MAX_FILE_SIZE = 15 * 1024 * 1024; // Maximum file size in bytes (15 MB)
export const CHAT_MAX_MESSAGE_SIZE = 2056; // Maximum message size in characters

interface ChatProps {
  id: number;
}

export default function Chat(chatProps: ChatProps) {
  const { id } = chatProps;

  const credentials = useCredentials();
  const { mutateAsync: fileUpload } = useChatFileUpload();

  const chatId = useChatStore((state) => state.chatId);
  const isDisconnected = useChatStore((state) => state.isDisconnected);
  const viewedFile = useChatStore((state) => state.viewedFile);
  const openViewer = useChatStore((state) => state.openViewer);

  const { mutate: readEntityNotifications } = usePostReadEntityNotifications();

  const [fsUploadState, fsUploadActions] = useFileUpload({
    maxSize: CHAT_MAX_FILE_SIZE,
    postponeUpload: false,
    onFileUploadError: (_file, _error, { code }) => {
      if (code === 'FILE_TOO_LARGE') {
        toast.error(`Plik jest za duży, spróbuj mniejszy plik. Maksymalny rozmiar to ${prettyBytes(CHAT_MAX_FILE_SIZE, { locale: 'pl-PL', binary: true })}`);
      }
    },
    uploadFileFn: async (file) => {
      await onFileUpload(file);
    },
  });

  const dropzoneConfig = fsUploadActions.createDropzoneProps();

  const setOpenViewer = (open: boolean) => {
    useChatStore.getState().setOpenViewer(open);
  };

  const onMessageUpload = ({ content, replyingTo }: messageFormTextProps) => {
    const tempUUID = uuidv4();

    const message: MessageSend = {
      id: -1,
      tempUUID: tempUUID,
      type: 'text',
      content: content,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      sender: credentials,
      replyingTo: replyingTo,
      replyingToId: replyingTo?.id,
      attachment: null,
      readReceipts: null,
    };

    chatSocketService.sendMessage(message);
  };

  const onFileUpload = async (file: File | undefined) => {
    if (!file) return;

    const serveType = FileTypeUtils.getServeFileType(file.type, {
      pick: [FileType.IMAGE, FileType.VIDEO, FileType.AUDIO],
    });
    const tempUUID = uuidv4();
    const store = useChatStore.getState();
    const socket = chatSocketService.getSocket();

    const message: MessageSend = {
      id: -1,
      type: serveType,
      sender: credentials,
      file: file,
      content: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      tempUUID: tempUUID,
      attachment: null,
      replyingTo: null,
      readReceipts: null,
    };

    store.appendMessages([message]);
    await fileUpload({
      id: id,
      file: file,
      type: serveType,
      content: file.name,
      tempUUID: tempUUID,
      socketId: socket?.id,
    })
      .then((response) => {
        store.confirmMessage(response);
      })
      .catch(() => {
        store.confirmMessage({
          action: 'failed',
          tempUUID: tempUUID,
          content: 'Nie udało się wysłać pliku',
          type: 'text',
          id: null,
          readReceipts: null,
        });
      });
  };

  const onSubmit = async ({ content, replyingTo }: messageFormProps) => {
    if (content) {
      onMessageUpload({ content, replyingTo });
    }
  };

  const onReconnect = () => {
    chatSocketService.reconnect(id);
  };

  useEffect(() => {
    if (id !== chatId) {
      chatSocketService.connect(id);
      readEntityNotifications({
        entityId: id,
        type: NotificationType.Chat,
      });
    }
  }, [chatId, id]);

  useEffect(() => {
    return () => {
      chatSocketService.disconnect();
    };
  }, []);

  return (
    <>
      <MediaViewer file={viewedFile ?? undefined} open={openViewer} onOpenChange={setOpenViewer} />
      <section className={cn('md:bg-background flex min-h-0 w-full min-w-0 flex-1 flex-col md:rounded-2xl md:border relative overflow-hidden')} {...dropzoneConfig.props}>
        <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col">
          {fsUploadState.isDragging && (
            <div
              className="bg-background/75 pointer-events-none absolute top-0 right-0 bottom-0 left-0 z-50 flex flex-col items-center justify-center gap-2 backdrop-blur-xs select-none"
              aria-hidden="true"
              tabIndex={-1}
              role="presentation"
            >
              <h4 className="text-foreground text-xl font-semibold">Upuść pliki tutaj</h4>
              <p className="text-muted-foreground text-sm">
                Maksymalny rozmiar pliku to{' '}
                {prettyBytes(CHAT_MAX_FILE_SIZE, {
                  locale: 'pl-PL',
                  binary: true,
                })}
              </p>
            </div>
          )}
          {isDisconnected && (
            <Button variant="tertiary" variantColor={'destructive'} size="sm" onClick={onReconnect} className="absolute top-20 left-1/2 z-50 w-fit -translate-x-1/2 shadow-md">
              <RotateCcwIcon />
              Spróbuj ponownie
            </Button>
          )}

          <ChatHeader id={id} />
          <ChatContent />
          <ChatFooter onSubmit={onSubmit} fsUploadActions={fsUploadActions} />
        </div>
      </section>
    </>
  );
}
